import type { ReactNode } from 'react'
import { Confetti } from '../../ice/art/Confetti'
import { ordinal } from '../../lib/format'
import type { LineupEntry } from './Lineup'

/** Podium heights and left-to-right order: silver, gold, bronze. */
const PODIUM = [
  { place: 2, height: 'h-9', top: 'bg-slate-300 dark:bg-slate-500' },
  { place: 1, height: 'h-14', top: 'bg-[var(--ice-brass)]' },
  { place: 3, height: 'h-7', top: 'bg-orange-300 dark:bg-orange-700' },
] as const

export interface PodiumEntry {
  place: number
  entry: LineupEntry
  /** Small line under the name ("12 moves · 4 pts"). */
  detail: string
}

interface Props {
  eyebrow: string
  title: string
  subtitle?: string | null
  /** Who stands where; empty hides the podium (nobody finished). */
  podium: PodiumEntry[]
  figure: (entry: LineupEntry, place: number) => ReactNode
  confetti?: boolean
  /** A footnote under the podium ("Still on the ice: …"). */
  note?: string | null
  /** The host's next action, or a waiting pill. */
  footer: ReactNode
}

/** A round's (or match's) podium card: headline, three steps, footnote and the host's next step. */
export function RoundResults({
  eyebrow,
  title,
  subtitle,
  podium,
  figure,
  confetti = false,
  note,
  footer,
}: Props) {
  const byPlace = new Map(podium.map((p) => [p.place, p]))
  return (
    <div className="relative flex flex-col items-center gap-3">
      {confetti && <Confetti />}
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-sky-600 dark:text-sky-400">
          {eyebrow}
        </div>
        <h2 className="text-xl font-black leading-tight">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>

      {podium.length > 0 && (
        <ol className="flex items-end justify-center gap-2" aria-label="Podium">
          {PODIUM.map(({ place, height, top }) => {
            const p = byPlace.get(place)
            return (
              <li key={place} className="flex w-20 flex-col items-center">
                {p ? (
                  <div className="flex flex-col items-center">
                    {figure(p.entry, place)}
                    <span className="max-w-20 truncate text-xs font-bold">{p.entry.nickname}</span>
                    <span className="text-[10px] text-slate-500">{p.detail}</span>
                  </div>
                ) : (
                  <div className="h-16" />
                )}
                <div
                  className={`mt-1 flex w-full flex-col items-center justify-start rounded-t-lg ${height} ${p ? top : 'bg-slate-100 dark:bg-slate-800'}`}
                >
                  <span className="pt-1 text-sm font-black text-slate-800">{ordinal(place)}</span>
                </div>
              </li>
            )
          })}
        </ol>
      )}

      {note && <p className="text-xs text-slate-500">{note}</p>}
      {footer}
    </div>
  )
}
